import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  LinearProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  Slider,
  IconButton
} from "@material-ui/core";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import { utcParse } from "d3-time-format";
import { scaleLinear, scaleTime } from "d3-scale";
import { extent } from "d3-array";
import moment from "moment";
import { createMuiTheme } from "@material-ui/core/styles";
import { ThemeProvider } from "@material-ui/styles";
import Chart from "./Chart";

const margin = { top: 10, right: 20, bottom: 20, left: 25 };

const height = 400 - margin.bottom - margin.top;
const width = 850 - margin.left - margin.right;
const parseTime = utcParse("%Y-%m-%dT%H:%M:%S.%LZ");

const depthStyle = { color: "#B60C0F", marginLeft: "25px" };
const timeStyle = { color: "grey", fontSize: "75%" };

const ChartBody = props => {
  const depthBoundsTheme = {
    color: "#AAAAFF",
    fill: "#AAAAFF",
    opacity: 0.3
  };
  const muiTheme = createMuiTheme({
    overrides: {
      MuiSlider: {
        thumb: {
          color: depthBoundsTheme.color
        },
        track: {
          color: depthBoundsTheme.color,
          opacity: depthBoundsTheme.opacity
        },
        rail: {
          color: "black",
          opacity: depthBoundsTheme.opacity
        }
      }
    }
  });
  let addStartData = () => {};
  let addEndData = () => {};
  let boundingBoxWidth = width;
  let boundingBoxMargin = 0;
  if (props.chartData) {
    const extentInst = extent(props.chartData, d => parseTime(d.measurementDt));
    const xScale = scaleTime()
      .domain(extentInst)
      .range([0, width]);
    boundingBoxWidth =
      xScale(
        moment(
          props.chartData[props.chartData.length - 1].measurementDt
        ).toDate()
      ) - xScale(moment(props.chartData[0].measurementDt).toDate());
    boundingBoxMargin =
      xScale(moment(props.chartData[0].measurementDt).toDate()) + 25;

    addStartData = () =>
      props.onAddStartDataClick({
        chartData: props.chartData,
        ctdFilePk: props.chartModalCast.ctdFilePk
      });

    addEndData = () =>
      props.onAddEndDataClick({
        chartData: props.chartData,
        ctdFilePk: props.chartModalCast.ctdFilePk
      });
  }
  return (
    <div>
      <ThemeProvider theme={muiTheme}>
        <Typography style={{ color: depthStyle.color }}>
          {`Bottom depth: ${Math.round(props.modalBottomDepth * 10) / 10} m `}{" "}
          <span style={timeStyle}>(detected at dashed line)</span>
        </Typography>
        <Typography variant="body1">
          Bottom time:{" "}
          {moment(props.modalBottomDatetime).format("dddd, MMMM Do YYYY,")}{" "}
          <span style={{ fontWeight: "bold" }}>
            {moment(props.modalBottomDatetime).format(" h:mm:ss a")}
          </span>
        </Typography>
        {props.chartModalOpen && !props.chartDataFetching ? (
          <div>
            <Typography variant="subtitle2" style={{ textAlign: "center" }}>
              {props.matchedForms[props.chartModalCast.ctdCastPk].castType ===
              "Static"
                ? "Select time bounds for detecting cast start, bottom, and end depths"
                : "Select time bounds for detecting cast start and bottom depths"}
            </Typography>
            <Box
              sx={{ width: boundingBoxWidth, marginLeft: boundingBoxMargin }}
            >
              <Slider
                sx={{ color: "#873423" }}
                value={props.depthBounds}
                min={props.defaultDatetimeMinEpoch}
                max={props.defaultDatetimeMaxEpoch}
                onChange={props.handleModalDepthBounds}
                valueLabelDisplay="off"
              />
            </Box>
          </div>
        ) : (
          <LinearProgress />
        )}
        {props.chartModalOpen && !props.chartDataFetching ? (
          <>
            <Chart
              height={height}
              width={width}
              margin={margin}
              data={props.chartData}
              x={scaleTime()}
              y={scaleLinear()}
              getX={d => parseTime(d.measurementDt)}
              getY={d => d.depth}
              rectData={props.chartModalCast}
              rectStartAccessor={c => parseTime(c.startDt)}
              rectEndAccessor={c =>
                parseTime(`${props.matchedForms[c.ctdCastPk].dropEndTime}Z`)
              }
              bottomDepth={-props.modalBottomDepth}
              bottomDatetime={props.modalBottomDatetime}
              depthBounds={props.depthBounds}
              depthBoundsTheme={depthBoundsTheme}
            />

            <Box display="flex" alignItems="center" justifyContent="center">
              <IconButton
                onClick={addStartData}
                disabled={props.chartModalCast.addStartDisabled}
              >
                <KeyboardDoubleArrowLeftIcon />
              </IconButton>
              <Typography variant="subtitle2" style={{ textAlign: "center" }}>
                Add Data
              </Typography>
              <IconButton
                onClick={addEndData}
                disabled={props.chartModalCast.addEndDisabled}
              >
                <KeyboardDoubleArrowRightIcon />
              </IconButton>
            </Box>
          </>
        ) : (
          <LinearProgress />
        )}
      </ThemeProvider>
    </div>
  );
};

const ChartModal = props => {
  let chartBodyProps = { ...props };
  let updateStoreDepthBounds = () => {};
  let handleModalDepthBounds = () => {};
  if (props.chartModalOpen && !props.chartDataFetching) {
    const [defaultDatetimeMinEpoch, defaultDatetimeMaxEpoch] = [
      props.chartData[0].measurementDt,
      props.chartData[props.chartData.length - 1].measurementDt
    ].map(x => moment(x).valueOf());
    const [defaultDatetimeStartEpoch, defaultDatetimeBottomEpoch] = [
      props.chartModalCast.startDt,
      props.chartModalCast.endDt ?? props.chartModalCast.bottomDt
    ].map(x => moment(x).valueOf());
    const [modalState, setModalState] = useState({
      modalBottomDatetime: props.chartModalCast.bottomDt,
      modalBottomDepth: props.chartModalCast.bottomDepth,
      modalStartDatetime: props.chartModalCast.startDt,
      modalStartDepth: props.chartModalCast.startDepth,
      modalEndDatetime: props.chartModalCast.endDt,
      modalEndDepth: props.chartModalCast.endDepth,
      depthBounds: [defaultDatetimeStartEpoch, defaultDatetimeBottomEpoch] // Use epoch for slider because requires int type
    });
    updateStoreDepthBounds = () => {
      const { ctdCastPk } = props.chartModalCast;
      const { castType } = props.matchedForms[ctdCastPk];

      return props.onChangeChartDepthBounds({
        ...modalState,
        manualOverrideDt:
          props.chartModalCast.manualOverrideDt ||
          props.chartModalCast.bottomDt !== modalState.modalBottomDatetime ||
          props.chartModalCast.startDt !== modalState.modalStartDatetime ||
          (props.chartModalCast.endDt !== modalState.modalEndDatetime &&
            castType === "Static"),
        ctdCastPk: props.chartModalCast.ctdCastPk,
        ctdFilePk: props.chartModalCast.ctdFilePk
      });
    };

    handleModalDepthBounds = (event, newWindow) => {
      const castData = props.chartData;
      const { ctdCastPk } = props.chartModalCast;
      const { castType } = props.matchedForms[ctdCastPk];
      const castSection = castData.filter(
        x =>
          moment(x.measurementDt).valueOf() >= newWindow[0] &&
          moment(x.measurementDt).valueOf() <= newWindow[1]
      );
      const newBottomDepth = -Math.min(...castSection.map(x => x.depth));
      const newBottomDt = castData.find(x => x.depth === -newBottomDepth)
        .measurementDt;

      const castStartSection = castSection.filter(
        x => moment(x.measurementDt) < moment(newBottomDt)
      );
      const newStartDepth = -Math.max(...castStartSection.map(x => x.depth));
      const newStartDt = castData.find(
        x =>
          x.depth === -newStartDepth &&
          moment(x.measurementDt) < moment(newBottomDt)
      ).measurementDt;

      let endState = {};
      if (castType === "Static") {
        const castEndSection = castSection.filter(
          x => moment(x.measurementDt) >= moment(newBottomDt)
        );
        const newEndDepth = -Math.max(...castEndSection.map(x => x.depth));
        const newEndDt = castData.find(
          x =>
            x.depth === -newEndDepth &&
            moment(x.measurementDt) >= moment(newBottomDt)
        ).measurementDt;
        endState = {
          modalEndDepth: newEndDepth,
          modalEndDatetime: newEndDt
        };
      }

      setModalState({
        ...modalState,
        ...{
          modalStartDepth: newStartDepth,
          modalStartDatetime: newStartDt,
          modalBottomDepth: newBottomDepth,
          modalBottomDatetime: newBottomDt,
          depthBounds: newWindow,
          ...endState
        }
      });
    };
    chartBodyProps = {
      ...props,
      ...modalState,
      ...{
        defaultDatetimeMinEpoch,
        defaultDatetimeMaxEpoch,
        handleModalDepthBounds
      }
    };
  }
  return (
    <Dialog open={props.chartModalOpen} maxWidth="md">
      <DialogTitle>Cast Detection Results</DialogTitle>
      <DialogContent>
        {props.chartModalOpen && !props.chartDataFetching ? (
          <ChartBody {...chartBodyProps} />
        ) : (
          <LinearProgress />
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={props.onChartModalClose}>
          Cancel
        </Button>
        {props.chartModalOpen && !props.chartDataFetching ? (
          <Button variant="text" onClick={updateStoreDepthBounds}>
            Accept
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

const dataShape = PropTypes.shape({
  measurementDt: PropTypes.string,
  depth: PropTypes.number
});

ChartModal.propTypes = {
  onChartModalClose: PropTypes.func.isRequired,
  onChangeChartDepthBounds: PropTypes.func.isRequired,
  chartModalOpen: PropTypes.bool.isRequired,
  chartModalCast: PropTypes.shape({
    ctdFilePk: PropTypes.number,
    ctdCastPk: PropTypes.number,
    startDt: PropTypes.string,
    bottomDt: PropTypes.string,
    endDt: PropTypes.string,
    deviceSn: PropTypes.string,
    bottomDepth: PropTypes.number,
    startDepth: PropTypes.number,
    endDepth: PropTypes.number,
    manualOverrideDt: PropTypes.bool,
    addStartDisabled: PropTypes.bool,
    addEndDisabled: PropTypes.bool
  }),
  chartDataFetching: PropTypes.bool.isRequired,
  chartData: PropTypes.arrayOf(dataShape),
  // eslint-disable-next-line react/forbid-prop-types
  possibleForms: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  matchedForms: PropTypes.object.isRequired
};

ChartModal.defaultProps = {
  // chartModalOpen: true,
  chartModalCast: {},
  // chartDataFetching: true,
  chartData: []
};

ChartBody.propTypes = {
  chartModalCast: ChartModal.propTypes.chartModalCast,
  chartData: ChartModal.propTypes.chartData,
  chartModalOpen: ChartModal.propTypes.chartModalOpen,
  chartDataFetching: ChartModal.propTypes.chartDataFetching,
  modalBottomDatetime: PropTypes.string.isRequired, // iso timestring for d3 to parse
  modalBottomDepth: PropTypes.number.isRequired,
  depthBounds: PropTypes.arrayOf(PropTypes.number).isRequired,
  defaultDatetimeMinEpoch: PropTypes.number.isRequired, // epoch time for slider
  defaultDatetimeMaxEpoch: PropTypes.number.isRequired,
  handleModalDepthBounds: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  matchedForms: PropTypes.object.isRequired,
  onAddStartDataClick: PropTypes.func.isRequired,
  onAddEndDataClick: PropTypes.func.isRequired
};

ChartBody.defaultProps = {
  ...ChartModal.defaultProps
};

export default ChartModal;
